import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MoreVertical,
  Eye,
  Download,
  Copy,
  Pencil,
  Trash2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { hasPermission } from "../../../utils/permissionUtils";

const MENU_WIDTH = 208;

const PaymentRowActionMenu = ({
  item,
  isOpen,
  onOpenChange,
  onView,
  onDownload,
  onCopy,
  onEdit,
  onDelete,
}) => {
  const buttonWrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  const canRead = hasPermission("finance.payment_requests", "R");
  const canCreate = hasPermission("finance.payment_requests", "C");
  const canDelete = hasPermission("finance.payment_requests", "D");
  const hasAnyAction = canRead || canCreate || canDelete;

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) {
      setMenuPos(null);
      return;
    }
    const left = Math.min(
      Math.max(8, rect.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - 8
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    const estimatedHeight = 280;
    const openUp = spaceBelow < estimatedHeight && rect.top > estimatedHeight;
    setMenuPos({
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      left,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!hasAnyAction || !isOpen) return undefined;
    const handleOutside = (e) => {
      const inButton = buttonWrapRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inButton && !inMenu) onOpenChange(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [hasAnyAction, isOpen, onOpenChange]);

  if (!hasAnyAction) return null;

  const itemClass =
    "flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-bold text-gray-600 hover:bg-gray-50 hover:text-gray-900";

  const closeThen = (fn) => {
    fn?.(item);
    onOpenChange(false);
  };

  const menuPanel = (
    <AnimatePresence>
      {isOpen && menuPos ? (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, scale: 0.95, y: 4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 4 }}
          style={{
            position: "fixed",
            top: menuPos.top,
            bottom: menuPos.bottom,
            left: menuPos.left,
            width: MENU_WIDTH,
            zIndex: 9999,
          }}
          className="rounded-2xl border border-gray-100 bg-white p-1.5 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {canRead ? (
            <>
              <button type="button" onClick={() => closeThen(onView)} className={itemClass}>
                <Eye size={15} className="text-red-600" />
                Xem chi tiết
              </button>
              <button type="button" onClick={() => closeThen(onDownload)} className={itemClass}>
                <Download size={15} className="text-indigo-600" />
                Tải phiếu
              </button>
            </>
          ) : null}

          {canCreate ? (
            <>
              <button type="button" onClick={() => closeThen(onCopy)} className={itemClass}>
                <Copy size={15} className="text-sky-600" />
                Copy
              </button>
              <button type="button" onClick={() => closeThen(onEdit)} className={itemClass}>
                <Pencil size={15} className="text-amber-600" />
                Sửa
              </button>
            </>
          ) : null}

          {canDelete ? (
            <button
              type="button"
              onClick={() => closeThen(onDelete)}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-bold text-red-600 hover:bg-red-50"
            >
              <Trash2 size={15} />
              Xóa
            </button>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <>
      <div
        className="relative inline-flex"
        ref={buttonWrapRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={buttonRef}
          type="button"
          title="Thao tác"
          aria-label="Mở menu thao tác"
          aria-expanded={isOpen}
          onClick={() => onOpenChange(!isOpen)}
          className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
            isOpen
              ? "border-gray-300 bg-gray-100 text-gray-700"
              : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          }`}
        >
          <MoreVertical size={18} />
        </button>
      </div>
      {typeof document !== "undefined"
        ? createPortal(menuPanel, document.body)
        : null}
    </>
  );
};

export default PaymentRowActionMenu;
